/**
 * Formulário pré-chat - Versão Premium
 * Com validação visual, animações e campo telefone com máscara
 * 
 * @version 2.0.0 - Adicionado suporte a require_name, require_phone com PhoneInput
 */

import React, { useState, useMemo } from 'react';
import { countryCodes, type CountryCode } from '@/lib/country-codes';
import { applyPhoneMask, isSpecialPhoneNumber } from '@/lib/input-masks';
import { validatePhone } from '@/lib/input-validators';

interface PreChatFormProps {
  onSubmit: (name: string, email: string, phone: string) => void;
  primaryColor: string;
  requireName: boolean;
  requireEmail: boolean;
  requirePhone: boolean;
  greetingMessage: string;
}

export const PreChatForm: React.FC<PreChatFormProps> = ({
  onSubmit,
  primaryColor,
  requireName,
  requireEmail,
  requirePhone,
  greetingMessage,
}) => {
  // Estados dos campos
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [selectedCountryCode, setSelectedCountryCode] = useState<CountryCode>(
    countryCodes.find(c => c.code === '+55') || countryCodes[0]
  );
  const [isSpecialNumber, setIsSpecialNumber] = useState(false);
  
  // Estados de validação
  const [errors, setErrors] = useState<{ name?: string; email?: string; phone?: string }>({});
  const [touched, setTouched] = useState<{ name?: boolean; email?: boolean; phone?: boolean }>({});
  const [focusedField, setFocusedField] = useState<string | null>(null);

  // Verifica se pelo menos um campo é obrigatório
  const hasRequiredFields = requireName || requireEmail || requirePhone;

  // Monta o número completo para envio
  const fullPhoneNumber = useMemo(() => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (isSpecialNumber) {
      return digits;
    }
    return `${selectedCountryCode.code}${digits}`;
  }, [phone, selectedCountryCode, isSpecialNumber]);

  // Valida formulário
  const validate = (): boolean => {
    const newErrors: { name?: string; email?: string; phone?: string } = {};
    
    // Validação do nome
    if (requireName) {
      if (!name.trim()) {
        newErrors.name = 'Nome é obrigatório';
      } else if (name.trim().length < 2) {
        newErrors.name = 'Nome muito curto';
      }
    }
    
    // Validação do email
    if (requireEmail) {
      if (!email.trim()) {
        newErrors.email = 'Email é obrigatório';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        newErrors.email = 'Email inválido';
      }
    } else if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Email inválido';
    }
    
    // Validação do telefone
    if (requirePhone) {
      if (!phone.trim()) {
        newErrors.phone = 'Telefone é obrigatório';
      } else {
        const phoneValidation = validatePhone(fullPhoneNumber);
        if (!phoneValidation.isValid) {
          newErrors.phone = phoneValidation.error || 'Telefone inválido';
        }
      }
    } else if (phone) {
      const phoneValidation = validatePhone(fullPhoneNumber);
      if (!phoneValidation.isValid) {
        newErrors.phone = phoneValidation.error || 'Telefone inválido';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submete formulário
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ name: true, email: true, phone: true });
    
    if (validate()) {
      onSubmit(name.trim(), email.trim(), fullPhoneNumber);
    }
  };

  // Handler para mudança de código de país
  const handleCountryCodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const code = countryCodes.find(c => c.code === e.target.value);
    if (code) {
      setSelectedCountryCode(code);
    }
  };

  // Handler para mudança do telefone com máscara
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const digits = rawValue.replace(/\D/g, '');
    
    // Detecta número especial (0800, 4003, etc.)
    const isSpecial = isSpecialPhoneNumber(digits);
    setIsSpecialNumber(isSpecial);
    
    // Aplica máscara
    const masked = applyPhoneMask(rawValue);
    setPhone(masked);
  };

  // Estilo do container
  const containerStyles: React.CSSProperties = {
    flex: 1,
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    animation: 'formFadeIn 0.3s ease-out',
    overflowY: 'auto',
  };

  // Estilo do greeting
  const greetingStyles: React.CSSProperties = {
    fontSize: '16px',
    color: '#374151',
    marginBottom: '24px',
    lineHeight: '1.6',
    textAlign: 'center',
  };

  // Estilo do grupo de input
  const inputGroupStyles: React.CSSProperties = {
    marginBottom: '20px',
  };

  // Estilo do label
  const labelStyles: React.CSSProperties = {
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  };

  // Estilo do input
  const getInputStyles = (hasError: boolean, isFocused: boolean): React.CSSProperties => ({
    width: '100%',
    padding: '12px 16px',
    border: `2px solid ${hasError ? '#ef4444' : isFocused ? primaryColor : '#e5e7eb'}`,
    borderRadius: '12px',
    fontSize: '15px',
    outline: 'none',
    transition: 'all 0.2s ease',
    boxSizing: 'border-box',
    backgroundColor: '#ffffff',
    boxShadow: isFocused ? `0 0 0 3px ${hasError ? '#ef444420' : `${primaryColor}15`}` : 'none',
  });

  // Estilo do select de código de país
  const getSelectStyles = (hasError: boolean, isFocused: boolean): React.CSSProperties => ({
    padding: '12px 8px',
    border: `2px solid ${hasError ? '#ef4444' : isFocused ? primaryColor : '#e5e7eb'}`,
    borderRadius: '12px 0 0 12px',
    borderRight: 'none',
    fontSize: '15px',
    outline: 'none',
    backgroundColor: '#f9fafb',
    cursor: 'pointer',
    minWidth: '90px',
  });

  // Estilo do input de telefone (lado direito)
  const getPhoneInputStyles = (hasError: boolean, isFocused: boolean): React.CSSProperties => ({
    flex: 1,
    padding: '12px 16px',
    border: `2px solid ${hasError ? '#ef4444' : isFocused ? primaryColor : '#e5e7eb'}`,
    borderRadius: '0 12px 12px 0',
    fontSize: '15px',
    outline: 'none',
    transition: 'all 0.2s ease',
    boxSizing: 'border-box',
    backgroundColor: '#ffffff',
    boxShadow: isFocused ? `0 0 0 3px ${hasError ? '#ef444420' : `${primaryColor}15`}` : 'none',
  });

  // Estilo do erro
  const errorStyles: React.CSSProperties = {
    fontSize: '12px',
    color: '#ef4444',
    marginTop: '6px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    animation: 'errorShake 0.3s ease-out',
  };

  // Estilo do botão
  const buttonStyles: React.CSSProperties = {
    width: '100%',
    padding: '14px',
    backgroundColor: primaryColor,
    color: '#ffffff',
    border: 'none',
    borderRadius: '12px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    marginTop: '8px',
    boxShadow: `0 4px 12px ${primaryColor}40`,
  };

  // Ícone de erro
  const ErrorIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );

  return (
    <>
      <style>
        {`
          @keyframes formFadeIn {
            0% { opacity: 0; transform: translateY(10px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          @keyframes errorShake {
            0%, 100% { transform: translateX(0); }
            25% { transform: translateX(-4px); }
            75% { transform: translateX(4px); }
          }
          .uaizychat-input::placeholder {
            color: #9ca3af;
          }
          .uaizychat-submit-btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 16px ${primaryColor}50 !important;
          }
          .uaizychat-submit-btn:active {
            transform: translateY(0);
          }
          .uaizychat-phone-container {
            display: flex;
            align-items: stretch;
          }
          .uaizychat-country-select {
            appearance: none;
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
            background-repeat: no-repeat;
            background-position: right 8px center;
            padding-right: 28px;
          }
        `}
      </style>
      
      <div style={containerStyles}>
        {/* Ícone de boas-vindas */}
        <div style={{
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          backgroundColor: `${primaryColor}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 16px',
        }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={primaryColor} strokeWidth="1.5">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        </div>
        
        <p style={greetingStyles}>{greetingMessage}</p>
        
        <form onSubmit={handleSubmit} noValidate>
          {/* Campo Nome - só mostra se requireName */}
          {requireName && (
            <div style={inputGroupStyles}>
              <label style={labelStyles}>
                Nome
                <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onFocus={() => setFocusedField('name')}
                onBlur={() => {
                  setFocusedField(null);
                  setTouched((t) => ({ ...t, name: true }));
                }}
                placeholder="Seu nome"
                className="uaizychat-input"
                style={getInputStyles(
                  Boolean(touched.name && errors.name),
                  focusedField === 'name'
                )}
                aria-invalid={touched.name && Boolean(errors.name)}
                aria-describedby={errors.name ? 'name-error' : undefined}
              />
              {touched.name && errors.name && (
                <span id="name-error" style={errorStyles} role="alert">
                  <ErrorIcon />
                  {errors.name}
                </span>
              )}
            </div>
          )}

          {/* Campo Email - só mostra se requireEmail */}
          {requireEmail && (
            <div style={inputGroupStyles}>
              <label style={labelStyles}>
                Email
                <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setFocusedField('email')}
                onBlur={() => {
                  setFocusedField(null);
                  setTouched((t) => ({ ...t, email: true }));
                }}
                placeholder="seu@email.com"
                className="uaizychat-input"
                style={getInputStyles(
                  Boolean(touched.email && errors.email),
                  focusedField === 'email'
                )}
                aria-invalid={touched.email && Boolean(errors.email)}
                aria-describedby={errors.email ? 'email-error' : undefined}
              />
              {touched.email && errors.email && (
                <span id="email-error" style={errorStyles} role="alert">
                  <ErrorIcon />
                  {errors.email}
                </span>
              )}
            </div>
          )}

          {/* Campo Telefone - só mostra se requirePhone */}
          {requirePhone && (
            <div style={inputGroupStyles}>
              <label style={labelStyles}>
                Telefone
                <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <div className="uaizychat-phone-container">
                {/* Select de código de país - esconde para números especiais */}
                {!isSpecialNumber && (
                  <select
                    value={selectedCountryCode.code}
                    onChange={handleCountryCodeChange}
                    className="uaizychat-country-select"
                    style={getSelectStyles(
                      Boolean(touched.phone && errors.phone),
                      focusedField === 'phone'
                    )}
                    aria-label="Código do país"
                  >
                    {countryCodes.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.flag} {country.code}
                      </option>
                    ))}
                  </select>
                )}
                <input
                  type="tel"
                  value={phone}
                  onChange={handlePhoneChange}
                  onFocus={() => setFocusedField('phone')}
                  onBlur={() => {
                    setFocusedField(null);
                    setTouched((t) => ({ ...t, phone: true }));
                  }}
                  placeholder={isSpecialNumber ? '0800 000 0000' : '(00) 00000-0000'}
                  className="uaizychat-input"
                  style={isSpecialNumber 
                    ? getInputStyles(Boolean(touched.phone && errors.phone), focusedField === 'phone')
                    : getPhoneInputStyles(Boolean(touched.phone && errors.phone), focusedField === 'phone')
                  }
                  aria-invalid={touched.phone && Boolean(errors.phone)}
                  aria-describedby={errors.phone ? 'phone-error' : undefined}
                />
              </div>
              {touched.phone && errors.phone && (
                <span id="phone-error" style={errorStyles} role="alert">
                  <ErrorIcon />
                  {errors.phone}
                </span>
              )}
            </div>
          )}

          {/* Botão Submit */}
          <button
            type="submit"
            style={buttonStyles}
            className="uaizychat-submit-btn"
          >
            Iniciar conversa
          </button>
        </form>
        
        {/* Texto de privacidade - só mostra se tem campos */}
        {hasRequiredFields && (
          <p style={{
            fontSize: '11px',
            color: '#9ca3af',
            textAlign: 'center',
            marginTop: '16px',
            lineHeight: '1.5',
          }}>
            Ao iniciar, você concorda com nossos termos de uso e política de privacidade.
          </p>
        )}
      </div>
    </>
  );
};
